/**
 * Shop-floor scanner diagnostics.
 *
 * The handheld has no devtools, so a scanner fault is normally diagnosed by
 * shipping a build and guessing. One earlier fix went as far as rendering a
 * debug panel on screen because there was nowhere else to put the information
 * (10287d33, reverted in be59342c). This sends it somewhere instead.
 *
 * WHAT TO LOG, AND WHY IT IS THE CAPTURE BOUNDARY. "The scanner does nothing"
 * is exactly the case where nothing downstream runs, so logging what the app
 * DID is useless. Log what ARRIVED, including what the validator threw away:
 *
 *   nothing logged  -> the device never delivered. Check its output mode.
 *   'rejected'      -> characters arrived and our validator dropped them.
 *   'captured'      -> input is fine, look further down.
 *
 * Fire and forget, always. Never await this in a scan path and never let it
 * throw: the operator is holding a board, and a logging failure must not become
 * a scanning failure.
 *
 * Rows expire after 14 days server-side, so this can stay indefinitely and
 * should be extended as more scanning surfaces are added: pass a new `surface`
 * and it just works, no schema change.
 */

import config from '../config';
import { getAccessToken } from '../apiHelpers';

export type DiagnosticSurface = 'badge' | 'assembly' | 'marker' | 'lookup' | 'ship';

interface Diagnostic {
  surface: DiagnosticSurface;
  event: string;
  raw_value?: string;
  detail?: Record<string, unknown>;
}

// Batched so a burst of keystroke-level events is one request, not thirty.
let queue: Diagnostic[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let operatorId: string | null = null;

/** Stamp subsequent diagnostics with the badged operator. */
export function setDiagnosticOperator(id: string | null) {
  operatorId = id;
}

async function flush() {
  flushTimer = null;
  if (queue.length === 0) return;

  const events = queue;
  queue = [];

  try {
    // Deliberately NOT apiFetchAuthed: that wrapper throws on non-2xx and
    // triggers the 401 refresh dance, neither of which belongs in a fire-and-
    // forget logger sitting in the scan path.
    //
    // But the endpoint sits behind the auth checkpoint like every other scanner
    // route, so the bearer token has to go on BY HAND. Shipping without it made
    // this whole table inert: every write 401'd and the catch below swallowed it,
    // so production logged nothing at all on the first day it was needed. If this
    // table is ever empty again, check here first.
    const token = getAccessToken();
    await fetch(`${config.API_ENDPOINT}/scanner/diagnostics`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
      body: JSON.stringify({ events, operator_id: operatorId }),
      keepalive: true,
    });
  } catch {
    // Swallowed on purpose. A scanner that cannot log is still a scanner.
  }
}

export function logScannerDiagnostic(d: Diagnostic) {
  try {
    queue.push(d);
    if (queue.length >= 20) {
      if (flushTimer) clearTimeout(flushTimer);
      void flush();
      return;
    }
    if (!flushTimer) flushTimer = setTimeout(() => void flush(), 2000);
  } catch {
    // Never throw from here.
  }
}
